import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Heart, 
  Target, 
  Eye, 
  CheckCircle2, 
  Users, 
  Scale, 
  Shield, 
  Gem,
  Clock,
  Recycle,
  FileText,
  BookOpen,
  ArrowRight
} from 'lucide-react';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer First",
      description: "We prioritize our customers' needs by providing quality paper products and stationery solutions."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Quality Assurance",
      description: "We stock only premium paper brands and maintain the highest quality standards for all our products."
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "Integrity",
      description: "We maintain transparent pricing and honest business practices in all our dealings."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Reliability",
      description: "We ensure consistent stock availability and timely delivery of all orders."
    },
    {
      icon: <Recycle className="w-6 h-6" />,
      title: "Sustainability",
      description: "We promote eco-friendly paper products and sustainable business practices."
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Education Support",
      description: "We're committed to supporting education by providing quality materials at competitive prices."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us | MyExpress Limited</title>
        <meta 
          name="description" 
          content="Learn about MyExpress's mission, vision, and values. Your trusted partner in quality paper and stationery supplies."
        />
      </Helmet>

      {/* Hero Section with red accent */}
      <div className="bg-gradient-to-r from-brand-red-600 to-brand-red-500 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About MyExpress</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            Your trusted source for premium paper products and office supplies
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Introduction with red accent border */}
        <div className="max-w-3xl mx-auto text-center mb-16 p-8 border-l-4 border-brand-red-500 bg-white shadow-lg rounded-lg">
          <p className="text-lg text-gray-600 leading-relaxed">
            Since our establishment, MyExpress has been dedicated to providing high-quality paper products, 
            stationery, and office supplies to businesses, schools, and individuals. We understand the 
            importance of reliable supplies in keeping your operations running smoothly and efficiently.
          </p>
        </div>

        {/* Mission, Vision, Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Mission */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow transform hover:-translate-y-1">
            <div className="w-12 h-12 bg-brand-red-50 rounded-lg flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-brand-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-gray-600">
              To provide our customers with premium quality paper products, stationery, and office supplies 
              at competitive prices. We aim to make sourcing essential supplies a seamless experience through 
              our expertise and commitment to customer satisfaction.
            </p>
          </div>

          {/* Vision */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow transform hover:-translate-y-1">
            <div className="w-12 h-12 bg-brand-red-50 rounded-lg flex items-center justify-center mb-4">
              <Eye className="w-6 h-6 text-brand-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
            <p className="text-gray-600">
              To be the leading paper and stationery supplier in Kenya, recognized for our comprehensive 
              inventory, competitive pricing, and exceptional customer service. We aim to support education 
              and business growth through quality products and sustainable practices.
            </p>
          </div>

          {/* Values Overview */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow transform hover:-translate-y-1">
            <div className="w-12 h-12 bg-brand-red-50 rounded-lg flex items-center justify-center mb-4">
              <Heart className="w-6 h-6 text-brand-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Promise</h2>
            <p className="text-gray-600">
              We promise to maintain consistent stock of quality papers, provide expert advice on 
              product selection, and ensure competitive pricing. Our commitment to excellence drives 
              everything we do.
            </p>
          </div>
        </div>

        {/* Core Values Section with red accent */}
        <div className="bg-brand-red-50 rounded-xl p-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg p-6 hover:shadow-lg transition-all group cursor-pointer"
              >
                <div className="w-12 h-12 bg-brand-red-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-red-500 transition-colors">
                  {React.cloneElement(value.icon, { className: "text-brand-red-500 group-hover:text-white transition-colors" })}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-r from-brand-red-600 to-brand-red-500 text-white rounded-xl p-8">
          <h2 className="text-3xl font-bold mb-4">Ready to Order?</h2>
          <p className="text-xl mb-6">
            Browse our extensive range of paper products or contact our team for bulk orders and special requirements.
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href="/products" 
              className="bg-white text-brand-red-500 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center"
            >
              View Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a 
              href="/contact" 
              className="bg-brand-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-brand-red-700 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;